import React, { useEffect, useState } from "react";
import Select from "react-select";
import Breadcrumb from "../../../components/Ui_Element/Breadcrumb";
import DotLoader from "../../../components/Ui_Element/DotLoader";
import { post, getlist, getById } from "../../../contexts/apicontexts";
import config from "../../../config";
import { showToast } from "../../../components/Ui_Element/CustomToast";
import { useTheme } from "../../../contexts/themContext";

export default function UserMenuPermissions() {
    const { isDarkMode } = useTheme();
    const [isLoder, setIsLoder] = useState(false);
    const [users, setUsers] = useState([]);
    const [rows, setRows] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedValue, setSelectedValue] = useState([]);

    useEffect(() => {
        fetchUsers();
        fetchUserPermissions();
    }, []);

    useEffect(() => {
        fetchUserPermissions();
    }, [selectedUser]);

    const fetchUsers = async () => {
        setIsLoder(true);
        try {
            const response = await getlist(`${config.baseurl}Users/getall`);
            if (response?.status === 200) {
                setUsers(response.data || []);
            }
        } catch (error) {
            console.error(error);
            showToast.error("Failed to load users.");
        } finally {
            setIsLoder(false);
        }
    };

    const fetchUserPermissions = async () => {
        setIsLoder(true);
        try {
            const url = selectedUser
                ? `${config.baseurl}MenuPermission/userMenu?userId=${selectedUser}`
                : `${config.baseurl}MenuPermission/userMenu`;
            const response = await getById(url);
            if (response?.status === 200) {
                setRows(response.data || []);
            }
        } catch (error) {
            console.error(error);
            showToast.error("Failed to load user menu permissions.");
        } finally {
            setIsLoder(false);
        }
    };

    const handleUserChange = (option) => {
        setSelectedUser(option?.value || null);
    };

    const userOptions = users.map((user) => ({
        value: user.id,
        label: `${user.fullName || user.name || user.email}${user.code ? ` (${user.code})` : ""}`,
        fullName: user.fullName || user.name || "",
        code: user.code || "",
        email: user.email || ""
    }));

    const userFilterOption = (option, rawInput) => {
        const q = (rawInput || "").trim().toLowerCase();
        if (!q) return true;
        const data = option.data || {};
        return (
            (option.label || "").toLowerCase().includes(q) ||
            (data.fullName || "").toLowerCase().includes(q) ||
            (data.code || "").toLowerCase().includes(q) ||
            (data.email || "").toLowerCase().includes(q)
        );
    };

    const togglePermission = (moduleIndex, permIndex, field) => {
        const updatedRows = [...rows];
        const permissionList =
            updatedRows[moduleIndex].rolePermissions ||
            updatedRows[moduleIndex].userPermissions ||
            [];
        permissionList[permIndex][field] = !permissionList[permIndex][field];
        setRows(updatedRows);

        const row = permissionList[permIndex];
        const updatedSelected = [...selectedValue];
        const existingIndex = updatedSelected.findIndex(item => item.menuName === row.menuName);

        if (existingIndex !== -1) {
            updatedSelected[existingIndex][field] = row[field];
            const { canAdd, canEdit, canDelete, canView, id } = updatedSelected[existingIndex];

            if (id === null && !canAdd && !canEdit && !canDelete && !canView) {
                updatedSelected.splice(existingIndex, 1);
            }
        } else {
            updatedSelected.push({
                id: row.id,
                menuId: row.menuId,
                menuName: row.menuName,
                canAdd: row.canAdd,
                canEdit: row.canEdit,
                canDelete: row.canDelete,
                canView: row.canView,
            });
        }

        setSelectedValue(updatedSelected);
    };

    const handleSave = async () => {
        if (!selectedUser) {
            showToast.warning("Please select a user first");
            return;
        }

        if (!selectedValue || selectedValue.length === 0) {
            showToast.warning("Please select at least one menu permission");
            return;
        }

        const menus = selectedValue.map(item => ({
            id: item.id,
            userId: selectedUser,
            menuId: item.menuId,
            canAdd: item.canAdd,
            canEdit: item.canEdit,
            canDelete: item.canDelete,
            canView: item.canView
        }));

        const payload = {
            userId: selectedUser,
            permissions: menus
        };

        try {
            setIsLoder(true);
            const response = await post(`${config.baseurl}MenuPermission/userMenu`, payload);

            if (response?.status === 200) {
                showToast.success("Permissions saved successfully.");
                setSelectedValue([]);
                fetchUserPermissions();
            }
        } catch (error) {
            console.error(error);
            showToast.error("Something went wrong.");
        } finally {
            setIsLoder(false);
        }
    };

    const selectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: "44px",
            borderRadius: "0.75rem",
            borderColor: state.isFocused ? "#C04C2A" : isDarkMode ? "#374151" : "#9CA3AF",
            backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
            color: isDarkMode ? "#f9fafb" : "#111827",
            boxShadow: state.isFocused ? "0 0 0 2px rgba(192,76,42,0.15)" : "none",
        }),
        singleValue: (base) => ({
            ...base,
            color: isDarkMode ? "#f9fafb" : "#111827",
        }),
        input: (base) => ({
            ...base,
            color: isDarkMode ? "#f9fafb" : "#111827",
        }),
        placeholder: (base) => ({
            ...base,
            color: isDarkMode ? "#9ca3af" : "#6b7280",
        }),
        menu: (base) => ({
            ...base,
            zIndex: 30,
            backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
            border: isDarkMode ? "1px solid #374151" : "1px solid #e5e7eb",
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused
                ? isDarkMode ? "#374151" : "#fff7ed"
                : isDarkMode ? "#1f2937" : "#ffffff",
            color: isDarkMode ? "#f3f4f6" : "#111827",
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
        }),
    };

    return (
        <div className="p-4 md:p-6 bg-gray-50 dark:bg-[#0F111A] min-h-screen transition-colors duration-300">
            <Breadcrumb pageName="User Menu Permissions" currentPage="User Menu Permissions" />

            <div className="bg-white dark:bg-[#161B22] rounded-xl shadow border dark:border-gray-800 mt-4 overflow-hidden">
                <div className="card-header flex justify-between items-center px-5 py-3 border-b transition-colors duration-300
                    bg-gradient-to-r from-[#C04C2A] to-[#4A1B13]
                    dark:from-[#2D120D] dark:to-[#1A1A1A] dark:border-gray-800">

                    <div className="flex items-center gap-3">
                        <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight">
                            User Menu Permissions
                        </h2>
                        {isLoder && <DotLoader color="#ffffff" size="w-1.5 h-1.5" />}
                    </div>
                </div>

                <div className="mt-4 p-2 transition-colors duration-300 dark:bg-[#161B22]">
                    <div className="flex flex-col md:flex-row gap-4 border-gray-400">
                        <div className="md:w-1/3 mb-4">
                            <Select
                                value={userOptions.find((option) => option.value === selectedUser) || null}
                                onChange={handleUserChange}
                                options={userOptions}
                                isSearchable
                                isClearable
                                placeholder="Select User"
                                className="w-full"
                                filterOption={userFilterOption}
                                menuPortalTarget={document.body}
                                styles={selectStyles}
                            />
                        </div>

                        <div className="md:w-2/3 space-y-4">
                            {rows.map((module, moduleIndex) => (
                                <div
                                    key={module.id}
                                    className="bg-gray-50 p-4 dark:bg-gray-800 rounded-xl shadow border border-gray-100 dark:border-gray-700"
                                >
                                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">
                                        {module.moduleName}
                                    </h3>

                                    {(module.rolePermissions || module.userPermissions || []).map((perm, permIndex) => (
                                        <div
                                            key={perm.menuId}
                                            className="flex flex-col md:flex-row md:items-center justify-between bg-white dark:bg-gray-700 p-2 rounded mb-2 border border-gray-100 dark:border-gray-600"
                                        >
                                            <span className="text-gray-800 dark:text-gray-200 mb-1 md:mb-0">
                                                {perm.menuName}
                                            </span>
                                            <div className="flex gap-2 flex-wrap text-gray-700 dark:text-gray-200">
                                                <label className="flex items-center gap-1">
                                                    <input
                                                        type="checkbox"
                                                        checked={perm.canAdd}
                                                        onChange={() => togglePermission(moduleIndex, permIndex, "canAdd")}
                                                        className="accent-green-500"
                                                    />
                                                    Add
                                                </label>
                                                <label className="flex items-center gap-1">
                                                    <input
                                                        type="checkbox"
                                                        checked={perm.canEdit}
                                                        onChange={() => togglePermission(moduleIndex, permIndex, "canEdit")}
                                                        className="accent-blue-500"
                                                    />
                                                    Edit
                                                </label>
                                                <label className="flex items-center gap-1">
                                                    <input
                                                        type="checkbox"
                                                        checked={perm.canDelete}
                                                        onChange={() => togglePermission(moduleIndex, permIndex, "canDelete")}
                                                        className="accent-red-500"
                                                    />
                                                    Delete
                                                </label>
                                                <label className="flex items-center gap-1">
                                                    <input
                                                        type="checkbox"
                                                        checked={perm.canView}
                                                        onChange={() => togglePermission(moduleIndex, permIndex, "canView")}
                                                        className="accent-indigo-500"
                                                    />
                                                    View
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}

                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={isLoder}
                                className="flex-1 px-4 py-3 mt-5 mb-5 text-white font-bold rounded-xl
                                    shadow-lg transform transition-all active:scale-95 disabled:opacity-70"
                                style={{
                                    background: "linear-gradient(to right, #C04C2A, #4A1B13)"
                                }}
                            >
                                {isLoder ? (
                                    <DotLoader color="#fff" size="w-1.5 h-1.5" />
                                ) : (
                                    "Save Menu Permissions"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
